// src/components/atomic-crm/contacts/useContactCompanyImport.tsx.js
import { useDataProvider, useGetIdentity, useStore } from "ra-core";
import { useCallback } from "react";
import { supabase } from "../providers/supabase/supabase";

// CSV columns — company fields are optional
export type ContactCompanyImportSchema = {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  title?: string;
  linkedin_url?: string;
  // Company columns (denormalized)
  company_name?: string;
  company_website?: string;
  company_sector?: string;
  company_size?: string;
};

// ── helpers ────────────────────────────────────────────────
const extractDomain = (url?: string): string | null => {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return null;
  }
};

const normalizeCompanyKey = (name?: string, website?: string): string | null => {
  const domain = extractDomain(website);
  if (domain) return domain;
  if (!name?.trim()) return null;
  return name.toLowerCase().trim().replace(/[^a-z0-9]/g, "");
};

// ── hook ───────────────────────────────────────────────────
export function useContactCompanyImport() {
  const dataProvider = useDataProvider();
  const user = useGetIdentity();
  const [autoEnrich] = useStore<boolean>(
    "crm.enrichment.autoOnImport",
      false,
  );

  const processBatch = useCallback(
    async (batch: ContactCompanyImportSchema[]) => {
      // 1. Get org_id
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Not authenticated");

      const { data: sale } = await supabase
        .from("sales")
        .select("org_id")
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (!sale?.org_id) throw new Error("No org_id found");
      const orgId = sale.org_id;

      // 2. Collect unique companies from the batch
      const uniqueCompanies = new Map<string, ContactCompanyImportSchema>();
      for (const row of batch) {
        const key = normalizeCompanyKey(row.company_name, row.company_website);
        if (!key) continue;
        if (!uniqueCompanies.has(key)) uniqueCompanies.set(key, row);
      }

      // 3. Fetch existing companies (match by name OR website)
      const companyKeys = [...uniqueCompanies.keys()];
      const existingCompanyMap = new Map<string, number>();

      if (companyKeys.length > 0) {
        const { data: existing } = await supabase
          .from("companies")
          .select("id, name, website")
          .eq("org_id", orgId);

        existing?.forEach((c: any) => {
          const key = normalizeCompanyKey(c.name, c.website);
          if (key) existingCompanyMap.set(key, c.id);
        });
      }

      // 4. Create missing companies
      for (const [key, row] of uniqueCompanies) {
        if (existingCompanyMap.has(key)) continue;

        const { data: newCompany } = await dataProvider.create("companies", {
          data: {
            name: row.company_name!.trim(),
          website: row.company_website,
          sector: row.company_sector,
          size: row.company_size,
          org_id: orgId,
      },
      });

        if (!newCompany?.id) continue;
        existingCompanyMap.set(key, newCompany.id);

        if (autoEnrich) {
          void supabase.from("enrichment_queue").insert({
            resource: "companies",
            record_id: newCompany.id,
            org_id: orgId,
            status: "pending",
          });
        }
      }

      // 5. Create contacts linked to their company
      await Promise.all(
        batch.map(async (row) => {
          const hasName = row.first_name?.trim() || row.last_name?.trim();
          const hasEmail = row.email?.trim();
          if (!hasName && !hasEmail) return; // skip blank rows

          const companyKey = normalizeCompanyKey(
            row.company_name,
            row.company_website,
          );
          const companyId = companyKey
            ? existingCompanyMap.get(companyKey)
            : undefined;

          const email_jsonb = row.email?.trim()
            ? [{ email: row.email.trim(), type: "Work" }]
            : [];
          const phone_jsonb = row.phone?.trim()
            ? [{ number: row.phone.trim(), type: "Work" }]
            : [];

          const { data: newContact } = await dataProvider.create("contacts", {
            data: {
              first_name: row.first_name?.trim() || "",
              last_name: row.last_name?.trim() || "",
              title: row.title,
              linkedin_url: row.linkedin_url,
              email_jsonb,
              phone_jsonb,
              company_id: companyId,
              sales_id: user?.identity?.id,
              org_id: orgId,
              first_seen: new Date().toISOString(),
              last_seen: new Date().toISOString(),
              status: "New",
            },
          });

          if (autoEnrich && newContact?.id) {
            void supabase.from("enrichment_queue").insert({
              resource: "contacts",
              record_id: newContact.id,
              org_id: orgId,
              status: "pending",
            });
          }
        }),
      );
    },
    [dataProvider, autoEnrich, user?.identity?.id],
  );

  return processBatch;
}